import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import { normalizeErrorMessages } from "api/common/utils/rtkq.utils";
import {
  useCreateMailListMutation,
  useGetMailConfigQuery,
  useListMailCategoriesQuery,
} from "api/memberPortal/mailList/mailList.api";
import { MailingListDomains } from "api/memberPortal/mailList/mailList.types";
import { useAppForm } from "components/Forms/AppForm";
import LoadingSpinner from "components/UI/LoadingSpinner/LoadingSpinner";
import { optionAlphaSort } from "utils/sort";

import { createMailListSchema, defaultMailListValues } from "./MailList.schema";

export default function MailListModal() {
  const navigate = useNavigate();
  const { data: categories, isLoading } = useListMailCategoriesQuery({
    limit: 100,
  });
  const { data: mailConfig, isLoading: isLoadingConfig } =
    useGetMailConfigQuery();

  const [createMailList, { isLoading: isCreating, error }] =
    useCreateMailListMutation();

  // Backend-owned, environment-specific label -> email domain map.
  const emailDomainByLabel = new Map(
    (mailConfig?.domains ?? []).map((d) => [d.label, d.emailDomain]),
  );

  const form = useAppForm({
    defaultValues: defaultMailListValues,

    validators: {
      onSubmit: createMailListSchema,
    },

    onSubmit: async ({ value }) => {
      const emailDomain = emailDomainByLabel.get(
        value.domain as MailingListDomains,
      );
      const fullEmail = `${value.email}@${emailDomain}`;

      const result = await createMailList({
        name: value.name,
        email: fullEmail,
        domain: value.domain as MailingListDomains,
        description: value.description,
        categoryId: value.category,
      });

      if (!("error" in result)) {
        navigate(`../details/${result.data.id}`);
      }
    },
  });

  const handleClose = () => {
    navigate("..");
  };

  if (isLoading || isLoadingConfig) return <LoadingSpinner />;
  if (!categories || !mailConfig) return null;

  const domainOptions = mailConfig.domains.map((d) => ({
    label: d.label,
    value: d.label,
  }));

  const categoriesDisplay = categories.results
    .map((cat) => ({
      value: cat.id,
      label: cat.name,
    }))
    .sort(optionAlphaSort);

  return (
    <Dialog open onClose={handleClose} fullWidth maxWidth="sm">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          Create Mailing List
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <form.AppField name="name">
              {(field) => <field.TextInput label="List Name" required />}
            </form.AppField>

            <form.AppField name="category">
              {(field) => (
                <field.SelectField
                  required
                  label="Category"
                  options={categoriesDisplay}
                />
              )}
            </form.AppField>

            <Stack direction="row" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <form.AppField name="email">
                  {(field) => <field.TextInput label="Email Prefix" required />}
                </form.AppField>
              </Box>

              <Box sx={{ flex: 1, alignSelf: "center" }}>
                <form.Subscribe selector={(state) => state.values.domain}>
                  {(domain) => (
                    <>@{emailDomainByLabel.get(domain as MailingListDomains)}</>
                  )}
                </form.Subscribe>
              </Box>
            </Stack>

            <form.AppField name="domain">
              {(field) => (
                <field.ToggleGroupSelect
                  label="Domain"
                  options={domainOptions}
                  required
                />
              )}
            </form.AppField>

            <form.AppField name="description">
              {(field) => (
                <field.TextInput label="Description" rows={2} required />
              )}
            </form.AppField>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, flexWrap: "wrap" }}>
          {error && (
            <Alert severity="error" sx={{ flex: "1 1 100%", mb: 2 }}>
              {normalizeErrorMessages(error)}
            </Alert>
          )}
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>

          <Button
            type="submit"
            variant="contained"
            disabled={isCreating}
            loading={isCreating}
          >
            + Create list
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
